import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { dashApi, type ReportSection } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtValue } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill } from "@/components/dash/ui";

// Metric rows can carry non-numeric values (e.g. unit "date" → "2026-03-16");
// coercing those through Number() renders "NaN", so fall back to the raw string.
function fmtStatValue(value: string | number, unit?: string): string {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? fmtValue(n, unit) : String(value);
}

function SectionView({ s }: { s: ReportSection }) {
  if (s.kind === "metrics") {
    return (
      <Panel title={s.title}>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-ink-500/40">
            {s.rows.map((r, i) => (
              <tr key={i} className="text-slate-300">
                <td className="py-2">{r.label}</td>
                <td className="py-2 text-right tabular-nums text-white">{r.value === null || r.value === undefined ? "—" : fmtStatValue(r.value, r.unit)}</td>
                <td className="py-2 pl-4 text-right text-xs text-slate-500">{r.change_pct === null || r.change_pct === undefined ? "" : `${r.change_pct > 0 ? "+" : ""}${r.change_pct}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    );
  }
  if (s.kind === "table") {
    return (
      <Panel title={s.title}>
        {!s.rows.length ? <EmptyState compact>No rows.</EmptyState> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                {s.columns.map((c) => <th key={c} className="pb-2 font-medium">{c}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {s.rows.map((row, i) => (
                <tr key={i} className="text-slate-300">
                  {row.map((c, j) => <td key={j} className="py-2 tabular-nums">{c === null ? "—" : String(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    );
  }
  return (
    <Panel title={s.title}>
      {!s.items.length ? <EmptyState compact>None.</EmptyState> : (
        <ul className="space-y-2">
          {s.items.map((it, i) => (
            <li key={i} className="flex items-start gap-2.5">
              {it.severity && <StatusPill status={it.severity} />}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-200">{it.title}</p>
                {it.detail && <p className="text-xs text-slate-500">{it.detail}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function BoardPackPage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const q = useQuery({ queryKey: ["board-pack"], queryFn: dashApi.boardPackPreview });
  const [ranId, setRanId] = useState<number | null>(null);
  const run = useMutation({ mutationFn: dashApi.boardPackRun, onSuccess: (r) => setRanId(r.report.id) });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Board Pack"
        description="A comprehensive monthly board-meeting report — executive summary, company/branch comparison, sales (incl/excl tax), repair performance (estimated value, kept separate), customer intelligence, target achievement, forecast accuracy, month-end projection, decision-centre summary, risks and data quality."
        right={
          <div className="flex items-center gap-2">
            {ranId && <a href={dashApi.reportExportUrl(ranId, "html")} target="_blank" rel="noreferrer" className="rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-ink-700">Open printable ↗</a>}
            {isAdmin && (
              <button type="button" onClick={() => run.mutate()} disabled={run.isPending}
                className="rounded-lg border border-brand-500/40 bg-brand-500/10 px-3 py-1.5 text-xs text-brand-200 hover:bg-brand-500/20 disabled:opacity-60">
                {run.isPending ? "Running…" : run.isSuccess ? "Generated ✓ — delivered" : "Generate & deliver"}
              </button>
            )}
          </div>
        }
      />
      <p className="text-xs text-slate-500">
        Preview generated live from the warehouse. Admins can generate &amp; deliver a persisted copy; everyone can Print / Save-as-PDF from the printable view. Repair value is shown estimated and is never added to business sales.
      </p>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data ? (
        <EmptyState>No board pack available.</EmptyState>
      ) : (
        <div className="space-y-6">
          <div className="text-sm text-slate-400">
            {q.data.title} · period {q.data.period_start} → {q.data.period_end}
          </div>
          {q.data.sections.map((s, i) => <SectionView key={i} s={s} />)}
        </div>
      )}
    </div>
  );
}
